import { vec3, quat } from 'gl-matrix';

export interface SettleFaceResult {
  normal: vec3;
  axisIndex: number;
}

/**
 * Select which AABB face should rest on the ground.
 * Scores each face by how much it faces down (dot with gravity) and
 * how stable the pose is (inverse distance from CoM to face).
 */
export function selectBestSettleFace(
  orientation: quat,
  centerOfMassOffset: vec3,
  localMin: vec3,
  localMax: vec3,
): SettleFaceResult {
  let com = centerOfMassOffset;
  let down = vec3.fromValues(0, -1, 0);

  let faces: [vec3, number][] = [
    [vec3.fromValues(-1, 0, 0), com[0] - localMin[0]],
    [vec3.fromValues(1, 0, 0), localMax[0] - com[0]],
    [vec3.fromValues(0, -1, 0), com[1] - localMin[1]],
    [vec3.fromValues(0, 1, 0), localMax[1] - com[1]],
    [vec3.fromValues(0, 0, -1), com[2] - localMin[2]],
    [vec3.fromValues(0, 0, 1), localMax[2] - com[2]],
  ];

  let bestScore = -Infinity;
  let bestNormal = faces[0][0];

  for (let [normal, comHeight] of faces) {
    let worldNormal = vec3.transformQuat(vec3.create(), normal, orientation);
    let facingDown = vec3.dot(worldNormal, down);
    if (facingDown <= 0) continue;
    let stability = 1 / (comHeight + 0.01);
    let score = facingDown * 0.3 + stability * 0.7;
    if (score > bestScore) {
      bestScore = score;
      bestNormal = normal;
    }
  }

  let axisIndex = Math.abs(bestNormal[0]) > 0.5 ? 0 : Math.abs(bestNormal[1]) > 0.5 ? 1 : 2;

  return { normal: bestNormal, axisIndex };
}

/**
 * Build a target quaternion that lays the object flat while preserving
 * its current yaw (rotation around Y). Uses swing-twist decomposition:
 * the current orientation is decomposed into yaw (twist around Y) and
 * tilt (swing away from upright). The target keeps the yaw but removes the tilt.
 *
 * @param bestNormal - Local-space face normal that should point down
 * @param bnIdx - Axis index of bestNormal (0=X, 1=Y, 2=Z)
 * @param orientation - Current body orientation
 * @param localMin - AABB min in local space
 * @param localMax - AABB max in local space
 */
export function buildSettleQuaternion(
  bestNormal: vec3,
  bnIdx: number,
  orientation: quat,
  localMin: vec3,
  localMax: vec3,
): quat {
  // Find the longest local axis perpendicular to bestNormal
  let dims = vec3.sub(vec3.create(), localMax, localMin);
  let longIdx: number;
  if (bnIdx === 0) longIdx = dims[1] >= dims[2] ? 1 : 2;
  else if (bnIdx === 1) longIdx = dims[0] >= dims[2] ? 0 : 2;
  else longIdx = dims[0] >= dims[1] ? 0 : 1;

  let longLocal = vec3.create();
  longLocal[longIdx] = 1;

  // Current world-space direction of the long axis, projected to XZ
  let longWorld = vec3.transformQuat(vec3.create(), longLocal, orientation);
  let longXZ = vec3.fromValues(longWorld[0], 0, longWorld[2]);
  if (vec3.length(longXZ) < 0.001) {
    longXZ = vec3.fromValues(1, 0, 0);
  }
  vec3.normalize(longXZ, longXZ);

  // Build orthogonal world-space target axes
  let worldDown = vec3.fromValues(0, -1, 0);
  let worldThird = vec3.cross(vec3.create(), longXZ, worldDown);
  vec3.normalize(worldThird, worldThird);
  let worldLong = vec3.cross(vec3.create(), worldDown, worldThird);
  vec3.normalize(worldLong, worldLong);

  // The three world-space targets for each local axis:
  //   bestNormal → worldDown (face on ground)
  //   longLocal → worldLong (preserves XZ direction)
  //   thirdLocal → computed to maintain right-handedness
  let bnSign = bestNormal[bnIdx]; // +1 or -1
  let worldBn = vec3.scale(vec3.create(), worldDown, bnSign);

  let thirdIdx = 3 - bnIdx - longIdx;

  // The third column must be cross(worldBn, worldLong) or cross(worldLong, worldBn)
  // depending on axis ordering to maintain right-handedness.
  // Right-hand rule: cross(col_i, col_j) = col_k when (i,j,k) is an even permutation of (0,1,2).
  let worldThirdSigned: vec3;
  let parity = (longIdx - bnIdx + 3) % 3 === 1 ? 1 : -1;
  if (parity > 0) {
    worldThirdSigned = vec3.cross(vec3.create(), worldBn, worldLong);
  } else {
    worldThirdSigned = vec3.cross(vec3.create(), worldLong, worldBn);
  }

  let cols: vec3[] = [vec3.create(), vec3.create(), vec3.create()];
  cols[bnIdx] = worldBn;
  cols[longIdx] = worldLong;
  cols[thirdIdx] = worldThirdSigned;

  // gl-matrix fromMat3 column-major: m[col*3+row], column j = where e_j maps
  let m = new Float32Array(9);
  for (let c = 0; c < 3; c++) {
    m[c * 3 + 0] = cols[c][0];
    m[c * 3 + 1] = cols[c][1];
    m[c * 3 + 2] = cols[c][2];
  }

  let target = quat.create();
  quat.fromMat3(target, m as any);
  quat.normalize(target, target);

  if (quat.dot(orientation, target) < 0) {
    quat.scale(target, target, -1);
  }

  return target;
}
